/**
 * Which page info tasks a reader may see — shared by the ⓘ dialog and the
 * Settings → Help guide.
 */
import { useMemo } from "react";

import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { PageInfoId } from "@/lib/i18n/types/pageInfo";
import { PAGE_INFO, type PageInfoGateContext, type PageInfoItemDef } from "./pageInfoRegistry";
import { buildPageInfoChunks, type PageInfoChunk } from "./search/pageInfoChunks";

export type PageInfoScope = Omit<PageInfoGateContext, "perms">;

/**
 * Every task the reader may see, on every page they may open. `page` is the
 * page the list is shown on: its own tasks are never hidden by its page gate
 * (the reader is already on it).
 */
export function useVisibleChunks(scope: PageInfoScope, page: PageInfoId | null): PageInfoChunk[] {
  const { t } = useLanguage();
  const perms = usePermissions();
  return useMemo(() => {
    const ctx = { perms, ...scope };
    return buildPageInfoChunks(t).filter((chunk) => {
      const pageDef = PAGE_INFO[chunk.page];
      if (chunk.page !== page && pageDef.visibleWhen && !pageDef.visibleWhen(ctx)) return false;
      const item = itemDef(chunk.page, chunk.item);
      return !item?.visibleWhen || item.visibleWhen(ctx);
    });
  }, [t, perms, scope, page]);
}

export function itemDef(page: PageInfoId, item: string): PageInfoItemDef<PageInfoId> | undefined {
  return (PAGE_INFO[page].items as ReadonlyArray<PageInfoItemDef<PageInfoId>>).find(
    (def) => def.id === item
  );
}
